import { useUser } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FirstVisitUnlockScreen } from "@/components/freemium/UnlockScreen";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { api } from "@/services/api";
import type { Assignment, EntitlementKey, Programme, Review, Submission } from "@/types";
import { Connections } from "../components/dashboard/Connections";
import { DashboardExtras } from "../components/dashboard/DashboardExtras";
import { NextActionStatus } from "../components/dashboard/NextActionStatus";
import { ProgrammeFeatures } from "../components/dashboard/ProgrammeFeatures";
import { ReportScoreChart } from "../components/dashboard/ReportScoreChart";
import { WeeklyBreakdown } from "../components/dashboard/WeeklyBreakdown";
import { WeeklyScoreList } from "../components/dashboard/WeeklyScoreList";
import { DashboardLayout } from "../layouts/DashboardLayout";

export default function DashboardPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { enrollment, isLoading: enrollmentLoading } = useEnrollment();
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [programme, setProgramme] = useState<Programme | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [entitlements, setEntitlements] = useState<EntitlementKey[]>([]);
  const loadingRef = useRef(false);

  const loadDashboardData = useCallback(async () => {
    // Prevent concurrent calls
    if (loadingRef.current) {
      return;
    }

    // Wait for enrollment to finish loading before proceeding
    if (enrollmentLoading) {
      return;
    }

    // If no enrollment found, redirect to registration
    if (!enrollment) {
      navigate("/register");
      return;
    }

    // If no user, return early
    if (!user) {
      return;
    }

    loadingRef.current = true;
    setIsDashboardLoading(true);
    try {
      // Single API call to get all dashboard data
      const dashboardData = await api.getDashboard(enrollment.user_id as string);

      // Transform programme data
      const programmeData: Programme = {
        id: dashboardData.programme.id,
        name: dashboardData.programme.name,
        status: dashboardData.programme.status as Programme["status"],
        start_date: dashboardData.programme.start_date,
        end_date: dashboardData.programme.end_date,
        total_weeks: dashboardData.programme.total_weeks,
        current_week: dashboardData.programme.current_week,
        is_draft: false,
        is_private: false, // TODO: Add is_private to dashboard API response
        price: 0, // TODO: Add price to dashboard API response
        created_at: "",
        updated_at: "",
      };
      setProgramme(programmeData);
      setEntitlements(dashboardData.entitlements);

      // Transform assignments (extract to flat arrays for components)
      const assignmentsData: Assignment[] = dashboardData.assignments.map((a) => ({
        id: a.id,
        programme_id: dashboardData.programme.id,
        week_number: a.week_number,
        assignment_number: a.assignment_number,
        start_date: a.start_date,
        deadline: a.deadline,
        time_status: a.time_status as Assignment["time_status"],
        topic_title: a.topic_title,
        created_at: "",
        updated_at: "",
      }));
      setAssignments(assignmentsData);

      // Extract submissions from assignments
      const submissionsData: Submission[] = dashboardData.assignments
        .filter((a) => a.submission)
        .map((a) => ({
          id: a.submission!.id,
          user_assignment_id: "", // Not needed for dashboard display
          topic_id: "", // Not needed for dashboard display
          file_path: "",
          revision_number: 1,
          previous_submission_id: null,
          submitted_at: a.submission!.submitted_at,
          created_at: "",
          updated_at: "",
          assignment_id: a.id, // Link back to assignment
        }));
      setSubmissions(submissionsData);

      // Extract reviews from assignments
      const reviewsData: Review[] = dashboardData.assignments
        .filter((a) => a.review)
        .map((a) => ({
          id: a.review!.id,
          submission_id: a.submission?.id ?? "",
          status: a.review!.status as Review["status"],
          error_message: null,
          sections: a.review!.sections,
          final_score: a.review!.final_score,
          rubric_version: null,
          prompt_version: null,
          admin_sections: null,
          admin_final_score: null,
          admin_reviewed_at: null,
          reviewed_by_id: null,
          reviewed_at: null,
          created_at: "",
          updated_at: "",
        }));

      setReviews(reviewsData);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      // If dashboard fails (e.g., no enrollment), redirect
      if ((error as any)?.status === 404) {
        navigate("/register");
      }
    } finally {
      setIsDashboardLoading(false);
      loadingRef.current = false;
    }
  }, [enrollment, enrollmentLoading, user, navigate]);

  // Reload dashboard data when enrollment changes
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Show loading spinner while enrollment or dashboard data is loading
  // Also don't render if we don't have all required data yet
  const isDataReady =
    !enrollmentLoading && !isDashboardLoading && programme && assignments.length > 0;

  if (!isDataReady) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-white/50" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <FirstVisitUnlockScreen enrollment={enrollment} />
      <div className="max-w-7xl mx-auto">
        {/* Next Action Status */}
        <NextActionStatus assignments={assignments} submissions={submissions} />

        {/* Mobile Layout: Single column with specific order */}
        <div className="flex flex-col gap-6 md:hidden">
          {/* Graph - Full width on mobile */}
          <ReportScoreChart assignments={assignments} submissions={submissions} reviews={reviews} />

          {/* Weekly Breakdown */}
          <WeeklyBreakdown
            program={programme}
            assignments={assignments}
            submissions={submissions}
            reviews={reviews}
          />

          {/* Weekly Score List */}
          <WeeklyScoreList
            program={programme}
            assignments={assignments}
            submissions={submissions}
            reviews={reviews}
          />

          {/* Connections - At the bottom on mobile */}
          <Connections />
          <ProgrammeFeatures entitlements={entitlements} />
          <DashboardExtras entitlements={entitlements} />
        </div>

        {/* Desktop Layout: 3-column grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {/* Left Column (Main Content) */}
          <div className="md:col-span-2 space-y-6">
            <ReportScoreChart
              assignments={assignments}
              submissions={submissions}
              reviews={reviews}
            />
            <WeeklyBreakdown
              program={programme}
              assignments={assignments}
              submissions={submissions}
              reviews={reviews}
            />
          </div>

          {/* Right Column (Side Content) */}
          <div className="space-y-6">
            <WeeklyScoreList
              program={programme}
              assignments={assignments}
              submissions={submissions}
              reviews={reviews}
            />
            <Connections />
            <ProgrammeFeatures entitlements={entitlements} />
            <DashboardExtras entitlements={entitlements} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
